import { createClient, SupabaseClient } from '@supabase/supabase-js';
import logger from './logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Create a dummy client or null - don't throw at module load time
let supabase: SupabaseClient | null = null;

try {
  // Only validate if we have actual values (not placeholders)
  const hasValidUrl = supabaseUrl && 
    supabaseUrl !== 'https://your-project-id.supabase.co' && 
    supabaseUrl.startsWith('http');
  const hasValidKey = supabaseAnonKey && 
    supabaseAnonKey !== 'your-anon-key-here' && 
    supabaseAnonKey.length > 20;

  if (hasValidUrl && hasValidKey) {
    // Säkerhetskontroll: service_role får ALDRIG användas i frontend
    if (supabaseAnonKey.includes('service_role')) {
      logger.security('Förbjuden nyckel upptäckt i frontend-konfiguration', { keyType: 'service_role' });
      throw new Error('service_role key får inte användas i frontend!');
    }

    logger.info('Supabase client initieras', { 
      url: supabaseUrl.replace(/https?:\/\/([^.]+).*/, 'https://$1.redacted'),
      hasAnonKey: true 
    });

    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        storageKey: 'caseflow-auth',
        storage: {
          getItem: (key: string) => {
            const value = sessionStorage.getItem(key);
            if (!value) return null;
            try {
              return JSON.parse(value);
            } catch {
              return value;
            }
          },
          setItem: (key: string, value: string) => {
            try {
              // Sanitize sensitive data before storing
              const parsed = JSON.parse(value);
              if (parsed?.user?.aud === 'authenticated') {
                // Don't store sensitive user data in plain text
                sessionStorage.setItem(key, JSON.stringify({
                  ...parsed,
                  user: { ...parsed.user, email: undefined, phone: undefined }
                }));
                return;
              }
            } catch {
              // Not JSON or no sensitive data, store as-is
            }
            sessionStorage.setItem(key, value);
          },
          removeItem: (key: string) => {
            sessionStorage.removeItem(key);
          },
        },
      },
    });
  } else {
    logger.warn('Supabase konfiguration saknas eller ogiltig - kör utan backend', {
      hasUrl: hasValidUrl,
      hasKey: hasValidKey,
      url: supabaseUrl?.substring(0, 20) + '...'
    });
  }
} catch (error) {
  logger.error('Fel vid initiering av Supabase client', { error });
}

// Export a mock or real client
export const getSupabase = () => {
  if (!supabase) {
    throw new Error('Supabase är inte konfigurerat. Kontrollera VITE_SUPABASE_URL och VITE_SUPABASE_ANON_KEY i .env.local');
  }
  return supabase;
};

// For backward compatibility, but will be null if not configured
export { supabase };
export type AuthUserWithProfile = any; // Simplified for now
